import { GrowthBook } from '@growthbook/growthbook'
import { Stripe } from '@stripe/stripe-js'
import { PageId } from 'constants/pages'
import firebase from 'firebase/compat/app'
import { IAdminCommand, IDogBreed, IDogsProfile } from 'models/common.model'
import { ILeadRaw, TPlans } from 'models/lead.model'
import {
  TPaymentOptions,
  TPaymentRequestButtons,
} from 'nove8/constants/payment'
import {
  Currency,
  GlobalError,
  InputValidationText,
  ProcessingKey,
} from 'nove8/constants/root-constants'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface IGlobalStore {
  startProcessing: (key: ProcessingKey) => void
  stopProcessing: (key: ProcessingKey) => void
  isProcessing: boolean
  processingKeys: ProcessingKey[]
  initialCohort: string // used only for the first lead requests, take cohort from lead for common use
  firebaseRemoteConfig: firebase.remoteConfig.RemoteConfig | null
  firebaseRemoteConfigVariantData: Record<string, string> | string | null
  growthbook: GrowthBook | null
  growthbookVariantData: Record<string, string> | string | null
  lead: ILeadRaw | null // don't use steps from lead, use hardcoded client steps config from the cohort
  cohortLeadCurrency: Currency
  cohortLeadPlans: TPlans | null
  breeds: IDogBreed[]
  commands: IAdminCommand[]
  inputEmail: string // used only for email page, after the page - take email from lead
  globalError: GlobalError | null
  isEventLoggerInitialized: boolean
  currentPageId: PageId | null
  currentPageIndex: number
  currentPageNumber: number
  accountEmailErrorMessage: InputValidationText | null
  accountPasswordErrorMessage: InputValidationText | null
  accountEmail: string | null
  stripePromise: Promise<Stripe | null> | null // legacy stripe promise
  stripePromiseCheckoutSession: Promise<Stripe | null> | null // stripe promise for Checkout Session
  stripePromiseCheckoutSessionPayPal: Promise<Stripe | null> | null // stripe promise for Checkout Session contains PayPal payment method
  checkoutSessionWithoutPaypalClientSecret: string
  checkoutSessionPaypalClientSecret: string
  isExpressCheckoutAvailable: boolean
  expressCheckoutAvailableButtons: TPaymentRequestButtons | 'unavailable' // for stripe-cs only
  stripeError: string
  stripeErrorCode: string | null
  isStripeLoaded: boolean
  isPaymentPageShown: boolean // Show the payment page
  isCancelOfferNotApplied: boolean
  isPaymentFlowsShown: boolean // Show payment methods on the payment page
  isApplePayOfferCtaShown: boolean
  isGooglePayOfferCtaShown: boolean
  isApplePayDirectPurchaseTracked: boolean
  paymentRequestOptions: TPaymentOptions | null
  isCustomEventSent: boolean
  isSessionStartEventSent: boolean
  dogProfile: Record<string, string> | null
  dogsProfile: IDogsProfile[] | null
  userHasChat: boolean
  woofyCreative: Record<string, string> | null
  previousWoofyChatSessionId: string
  geoRegion: string | null
  geoCity: string | null
  isLastChanceOfferOpen: boolean
}

const INITIAL_STORE: IGlobalStore = {
  startProcessing: () => undefined,
  stopProcessing: () => undefined,
  isProcessing: false,
  processingKeys: [],
  initialCohort: '',
  firebaseRemoteConfig: null,
  firebaseRemoteConfigVariantData: null,
  growthbook: null,
  growthbookVariantData: null,
  lead: null,
  cohortLeadCurrency: Currency.USD,
  cohortLeadPlans: null,
  breeds: [],
  commands: [],
  inputEmail: '',
  globalError: null,
  isEventLoggerInitialized: false,
  currentPageId: null,
  currentPageIndex: -1,
  currentPageNumber: 0,
  accountEmailErrorMessage: null,
  accountPasswordErrorMessage: null,
  accountEmail: null,
  stripePromise: null,
  stripePromiseCheckoutSession: null,
  stripePromiseCheckoutSessionPayPal: null,
  isExpressCheckoutAvailable: false,
  checkoutSessionWithoutPaypalClientSecret: '',
  checkoutSessionPaypalClientSecret: '',
  expressCheckoutAvailableButtons: 'unavailable',
  stripeError: '',
  isStripeLoaded: false,
  isPaymentPageShown: false,
  isCancelOfferNotApplied: false,
  isPaymentFlowsShown: false,
  isApplePayOfferCtaShown: false,
  isGooglePayOfferCtaShown: false,
  isApplePayDirectPurchaseTracked: false,
  paymentRequestOptions: null,
  isCustomEventSent: false,
  isSessionStartEventSent: false,
  stripeErrorCode: null,
  dogProfile: null,
  dogsProfile: null,
  userHasChat: false,
  woofyCreative: null,
  previousWoofyChatSessionId: '',
  geoRegion: null,
  geoCity: null,
  isLastChanceOfferOpen: false,
}

export const useZustandStore = create<IGlobalStore>()(
  devtools((set) => ({
    ...INITIAL_STORE,
    startProcessing: (key: ProcessingKey) =>
      set((state) => ({
        isProcessing: true,
        processingKeys: state.processingKeys.concat(key),
      })),
    stopProcessing: (key: ProcessingKey) =>
      set((state) => {
        const processingKeys = state.processingKeys.filter(
          (processingKey) => processingKey && processingKey !== key,
        )
        return {
          isProcessing: !!processingKeys.length,
          processingKeys,
        }
      }),
  })),
)
export const useZustandGlobalStore = useZustandStore
